import React, { useState, useEffect } from "react";
import { api, errMsg, COUNTRIES } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/Primitives";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, ExternalLink } from "lucide-react";

export default function CompanySettings() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publicUrl, setPublicUrl] = useState("");
  useEffect(() => { api.get("/company").then(({ data }) => setC(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/company/public-link").then(({ data }) => setPublicUrl(`${window.location.origin}/enquiry/${data.slug}`)).catch(() => {});
  }, []);
  const up = (k, v) => setC({ ...c, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", {
        name: c.name, website: c.website, country: c.country, address: c.address,
        contact_number: c.contact_number, email: c.email, business_type: c.business_type,
        gross_margin: Number(c.gross_margin), logo: c.logo, accent_color: c.accent_color,
      });
      toast.success("Company settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!c) return <div className="py-16 flex justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" /></div>;

  return (
    <div>
      <PageHeader title="Company Settings" subtitle="Manage your company profile and export preferences" />
      {publicUrl && (
        <Card className="border border-sky-200 bg-sky-50/50 rounded-xl shadow-sm p-5 mb-6 max-w-3xl" data-testid="public-link-card">
          <p className="text-xs uppercase tracking-wider font-semibold text-sky-700">Public Enquiry Page</p>
          <p className="text-sm text-slate-600 mt-1">Share this link with international buyers. Every enquiry lands in your Leads automatically.</p>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <Input readOnly value={publicUrl} className="bg-white font-mono text-xs" data-testid="public-link-input" />
            <Button variant="outline" className="gap-2 flex-shrink-0" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }} data-testid="copy-public-link"><Copy className="h-4 w-4" />Copy</Button>
            <Button variant="outline" className="gap-2 flex-shrink-0" onClick={() => window.open(publicUrl, "_blank")} data-testid="open-public-link"><ExternalLink className="h-4 w-4" />Open</Button>
          </div>
        </Card>
      )}
      <Card className="border border-slate-200/80 rounded-xl shadow-sm p-6 max-w-3xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2"><Label>Company name</Label><Input value={c.name || ""} onChange={(e) => up("name", e.target.value)} className="bg-white" data-testid="settings-name" /></div>
          <div className="space-y-1.5"><Label>Website</Label><Input value={c.website || ""} onChange={(e) => up("website", e.target.value)} className="bg-white" /></div>
          <div className="space-y-1.5"><Label>Country</Label>
            <Select value={c.country || ""} onValueChange={(v) => up("country", v)}><SelectTrigger className="bg-white" data-testid="settings-country"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent className="bg-white max-h-60">{COUNTRIES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label>Contact number</Label><Input value={c.contact_number || ""} onChange={(e) => up("contact_number", e.target.value)} className="bg-white" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={c.email || ""} onChange={(e) => up("email", e.target.value)} className="bg-white" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Address</Label><Input value={c.address || ""} onChange={(e) => up("address", e.target.value)} className="bg-white" /></div>
          <div className="space-y-1.5"><Label>Business type</Label>
            <Select value={c.business_type || "both"} onValueChange={(v) => up("business_type", v)}><SelectTrigger className="bg-white" data-testid="settings-type"><SelectValue /></SelectTrigger><SelectContent className="bg-white"><SelectItem value="manufacturer">Manufacturer</SelectItem><SelectItem value="exporter">Exporter</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label>Default gross margin (%)</Label><Input type="number" value={c.gross_margin ?? 25} onChange={(e) => up("gross_margin", e.target.value)} className="bg-white" data-testid="settings-margin" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Logo URL (for public enquiry page)</Label><Input value={c.logo || ""} onChange={(e) => up("logo", e.target.value)} placeholder="https://…/logo.png" className="bg-white" data-testid="settings-logo" /></div>
          <div className="space-y-1.5"><Label>Brand accent color</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={c.accent_color || "#0284c7"} onChange={(e) => up("accent_color", e.target.value)} className="bg-white h-10 w-16 p-1 cursor-pointer" data-testid="settings-accent" />
              <span className="text-sm text-slate-500 font-mono">{c.accent_color || "#0284c7"}</span>
            </div>
          </div>
        </div>
        <div className="mt-6"><Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="settings-save-button">{saving ? "Saving…" : "Save changes"}</Button></div>
      </Card>
    </div>
  );
}
